import React, { useEffect, useRef, useState, useMemo } from 'react';
import { API_BASE_URL } from '../config';
import TroupeBriefCards from './TroupeBriefCards';

const generateThreadId = () => `horatio_panel_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

const CHAR_SLUG = {
  falstaff: 'falstaff',
  iago: 'iago',
  'lady macbeth': 'lady_macbeth',
  miranda: 'miranda',
  'sir andrew': 'sir_andrew',
};

function renderInline(text) {
  try {
    const t = String(text || '');
    const parts = [];
    const re = /(https?:\/\/[^\s\]]+)|\*([^*]+)\*/g;
    let last = 0;
    let m;
    let k = 0;
    while ((m = re.exec(t)) !== null) {
      if (m.index > last) parts.push(t.slice(last, m.index));
      if (m[1]) {
        parts.push(
          <a
            key={`link-${k++}`}
            href={m[1]}
            target="_blank"
            rel="noopener noreferrer"
            className="horatio-inline-link"
          >
            Open on Bluesky
          </a>
        );
      } else if (m[2]) {
        parts.push(<em key={`em-${k++}`}>{m[2]}</em>);
      }
      last = re.lastIndex;
    }
    if (last < t.length) parts.push(t.slice(last));
    return parts;
  } catch {
    return String(text || '');
  }
}

function detectPlay(text) {
  const t = (text || '').toLowerCase();
  const plays = [
    { pattern: /\bothello\b/, name: 'Othello' },
    { pattern: /\bhamlet\b/, name: 'Hamlet' },
    { pattern: /\bmacbeth\b/, name: 'Macbeth' },
    { pattern: /\bking lear\b|\blear\b/, name: 'King Lear' },
    { pattern: /\brichard ii\b|\brichard 2\b/, name: 'Richard II' },
    { pattern: /\brichard iii\b|\brichard 3\b/, name: 'Richard III' },
    { pattern: /\bhenry v\b|\bhenry 5\b/, name: 'Henry V' },
    { pattern: /\bromeo\b|\bjuliet\b/, name: 'Romeo and Juliet' },
    { pattern: /\btwelfth night\b/, name: 'Twelfth Night' },
    { pattern: /\bmidsummer\b/, name: "A Midsummer Night's Dream" },
    { pattern: /\bmerchant of venice\b/, name: 'The Merchant of Venice' },
    { pattern: /\bmuch ado\b/, name: 'Much Ado About Nothing' },
    { pattern: /\bjulius caesar\b/, name: 'Julius Caesar' },
    { pattern: /\bantony and cleopatra\b|\bantony\b/, name: 'Antony and Cleopatra' },
  ];
  for (const p of plays) {
    if (p.pattern.test(t)) return p.name;
  }
  return '';
}

function followupsFromCards(cards) {
  const first = (cards || []).find((c) => c && c.excerpt);
  if (!first) return [];
  const clip = String(first.excerpt).slice(0, 90);
  const who = first.character || 'they';
  const slug = CHAR_SLUG[(who || '').toLowerCase()] || first.slug;
  const items = [
    { label: 'Why that line?', query: `Why did ${who} write: "${clip}"` },
    { label: 'A parallel in the play', query: `What Shakespeare passage sits beside this line: "${clip}"` },
  ];
  if (slug) {
    items.push({ label: `Talk to ${who}`, href: `#characters?char=${slug}` });
  }
  return items;
}

export default function HoratioPanel() {
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [playScope, setPlayScope] = useState('');
  const threadId = useMemo(() => generateThreadId(), []);
  const endRef = useRef(null);
  const empty = messages.length === 0;

  useEffect(() => {
    try {
      if (endRef.current) endRef.current.scrollIntoView({ behavior: 'smooth' });
    } catch {}
  }, [messages, suggestions, busy]);

  useEffect(() => {
    (async () => {
      try {
        await fetch(`${API_BASE_URL}/api/auth/token`, { method: 'POST', credentials: 'include' });
      } catch {}
    })();
  }, []);

  const send = async (overrideText) => {
    if (busy) return;
    if (overrideText && typeof overrideText !== 'string') overrideText = undefined;
    const q = String(overrideText !== undefined ? overrideText : query || '').trim();
    if (!q) return;

    const detectedPlay = detectPlay(q);
    if (detectedPlay) setPlayScope(detectedPlay);
    try {
      setBusy(true);
      setSuggestions([]);
      setMessages((prev) => [...prev, { role: 'user', content: q }]);
      const body = {
        message: q,
        thread_id: threadId,
        user_id: 'horatio_panel_user',
      };
      const r = await fetch(`${API_BASE_URL}/api/horatio/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const txt = await r.text();
        const msg = `Chat error (${r.status}): ${txt.slice(0, 140)}`;
        setMessages((prev) => [...prev, { role: 'assistant', content: msg, cards: [] }]);
        setQuery('');
        return;
      }
      const dj = await r.json();
      const rep = (dj && (dj.response || dj.reply)) || '';
      const cards = Array.isArray(dj && dj.cards) ? dj.cards : [];
      setMessages((prev) => [...prev, { role: 'assistant', content: rep, cards }]);
      setSuggestions(Array.isArray(dj && dj.suggestions) ? dj.suggestions : []);
      if (window.gtag) {
        window.gtag('event', 'horatio_query', {
          has_suggestions: (dj.suggestions || []).length > 0,
          has_cards: cards.length > 0,
        });
      }
      setQuery('');
    } catch {
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: 'I could not reach the Memory Agent. Try again in a moment.', cards: [] },
      ]);
    } finally {
      setBusy(false);
    }
  };

  const lastAssistant = [...messages].reverse().find((m) => m.role === 'assistant');
  const chipItems = lastAssistant && lastAssistant.cards && lastAssistant.cards.length
    ? followupsFromCards(lastAssistant.cards)
    : (suggestions || []).map((s) => ({ label: s.label || s.query, query: s.query }));

  return (
    <div className="horatio-panel">
      <p className="horatio-lede">
        Horatio is the Troupe’s Memory Agent. Ask about a passage, or what they have been posting.
      </p>

      <div className="horatio-compose">
        {empty && (
          <div className="horatio-doors">
            <button
              type="button"
              className="btn-dark"
              onClick={() => send('What is ocular proof in Othello?')}
              disabled={busy}
            >
              A passage
            </button>
          </div>
        )}
        <div className="horatio-input-row">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            placeholder="Ask about a passage, or what the troupe has posted"
            aria-label="Ask Horatio"
          />
          <button disabled={busy || !query.trim()} onClick={() => send()} className="btn-dark">
            {busy ? 'Asking…' : 'Ask'}
          </button>
        </div>
      </div>

      {!empty && (
        <div className="horatio-thread">
          {messages.map((m, idx) => (
            <div key={idx} className={`horatio-turn horatio-turn-${m.role}`}>
              <div className="horatio-who">{m.role === 'user' ? 'You' : 'Horatio'}</div>
              <div className="horatio-body">
                {m.role === 'assistant' ? renderInline(m.content) : String(m.content || '')}
              </div>
              {m.role === 'assistant' && m.cards && m.cards.length > 0 && (
                <TroupeBriefCards cards={m.cards} />
              )}
            </div>
          ))}
          <div ref={endRef} />
        </div>
      )}

      {chipItems.length > 0 && !empty && (
        <div className="horatio-next">
          <div className="horatio-next-label">Ask next</div>
          {chipItems.map((sug, i) =>
            sug.href ? (
              <a key={i} href={sug.href} className="btn-dark horatio-next-link">
                {sug.label}
              </a>
            ) : (
              <button
                key={i}
                type="button"
                className="btn-dark"
                onClick={() => {
                  try {
                    const cleaned = String(sug.query || '').replace(/\s+in\s+[A-Za-z][A-Za-z\s]+$/i, '').trim();
                    const withScope = playScope ? `${cleaned} in ${playScope}` : cleaned;
                    send(withScope || String(sug.query || ''));
                  } catch {
                    send(String(sug.query || ''));
                  }
                }}
              >
                {sug.label}
              </button>
            )
          )}
        </div>
      )}
    </div>
  );
}
